// shared-lib/cabinet-runs/upperHeights.js
//
// The heights ONE upper cabinet may take — the one rule every surface that
// sets an upper's height asks: the bay panel's Height row, the override write
// boundary and the fill itself, which builds a stored height only when this
// rule allows it. The upper-tier twin of `bayWidths.js`. Pure.
//
// What a height has to be (plan 2026-10-21, C1–C6):
//  - a height the LINE makes at this box's width — read through
//    `resolveCabinetSku`, the same reading the estimate prices it by, so the
//    chip, the drawing and the price cannot disagree. A family with no size
//    catalog offers every stock height;
//  - no taller than the run's own (Decided 2 — a bay is never taller than its
//    run; the run's height is the run's to set);
//  - and it has to fit under the ceiling. A ceiling-trimmed height is
//    offered by no family (user ruling 2026-10-21: "you cant build a 40" if
//    the prod family doesnt offer it").
//
// A height that fails is never built in its place by splitting or dropping
// the box: the fill builds the run's height and says why (`bayHeightFor`).

const { familyOf } = require('./families');
const {
  familySizes,
  resolveCabinetSku,
  cabinetKindOf,
  snapUpperHeightIn,
  unitsOf,
} = require('./vendorSizes');
const { profileOf } = require('./profiles');

const IN = 0.0254;

/** The stock wall-cabinet heights — what a family with no catalog offers. */
const STOCK_UPPER_HEIGHTS_IN = Object.freeze([12, 15, 18, 24, 30, 36, 42]);

/** Two heights this close (inches) are the same height. */
const SAME_IN = 1e-6;
/**
 * Slack on "fits under the ceiling": an inch. A ceiling is SCANNED, and a
 * scan is read to the inch (plan C8); with less, an 8' kitchen whose ceiling
 * reads 95.9" could not take the 42" uppers it plainly has room for. The band
 * builds such a height whole and drops its bottom by the shortfall
 * (`upperBand.cutToLine`).
 */
const CEILING_SLACK_IN = 1;
/** An upper is 12" deep. */
const UPPER_DEPTH_M = 12 * IN;

/** A height in inches as a sentence says it: 36", 15.5". */
const said = (n) => `${+n.toFixed(2)}"`;
/** Half-inch precision, as the catalog reads a box (`resolveSku`). */
const halfIn = (m) => Math.round((m / IN) * 2) / 2;
/** An eighth of an inch: how a measured band or ceiling is read. */
const eighthIn = (m) => Math.round((m / IN) * 8) / 8;

/** "a", "a or b", "a, b or c" — inches. */
function listSaid(values) {
  const s = values.map(said);
  if (s.length <= 1) return s.join('');
  return `${s.slice(0, -1).join(', ')} or ${s[s.length - 1]}`;
}

/**
 * A plain 12"-deep upper of this size, for asking the resolver about a box
 * the fill has planned but not built. `blind` reads as a blind corner wall
 * cabinet, as `buildUpperBox` labels one; `filler` / `openShelf` as the boxes
 * no line sells by size (`isSoldUpper`); `glass` as a box with glass doors,
 * which a vendor makes at fewer heights (its glass door has to exist too).
 */
function upperProbe(
  widthM,
  heightM,
  {
    blind = false,
    filler = false,
    openShelf = false,
    floatingShelves = false,
    endShelf = false,
    glass = false,
  } = {}
) {
  return {
    type: 'upper-cabinet',
    // drawn with glass doors, the way the fill will build it (`glassUppers`)
    ...(glass ? { doorStyle: 'glass-front' } : {}),
    category: {
      'upper-cabinet': true,
      ...(blind ? { blindCorner: true } : {}),
      ...(filler ? { filler: true } : {}),
      ...(openShelf ? { openShelf: true } : {}),
      ...(floatingShelves ? { floatingShelves: true } : {}),
      ...(endShelf ? { endShelf: true } : {}),
    },
    dimensions: { x: widthM, y: heightM, z: UPPER_DEPTH_M },
  };
}

/**
 * Is this box a unit the line SELLS by size? A filler is ripped from stock and
 * an open shelf is the box with no doors; the fill hangs neither at a line
 * height (`upperTier.buildUpperBox` snaps only the rest), so no line limits
 * their height and their run height is the band's.
 */
const isSoldUpper = (box) =>
  !box?.category?.filler &&
  !box?.category?.openShelf &&
  !box?.category?.floatingShelves;

/**
 * The tallest height (inches) `family` OFFERS a wall box of `kind`, `widthIn`
 * wide, in a slot `slotIn` tall — or null when it offers none that fits.
 * Under a size catalog, the heights its line makes at that width
 * (`snapUpperHeightIn`); with none, the stock heights every family offers
 * (`STOCK_UPPER_HEIGHTS_IN` — the upper Height chips). The one rule a stacked
 * row is built by (user ruling 2026-10-21: "it should only allow products
 * available in the product family"): never a box trimmed to fit a gap.
 */
function offeredUpperHeightIn(family, kind, widthIn, slotIn) {
  if (familySizes(family))
    return snapUpperHeightIn(family, kind, widthIn, slotIn);
  const fits = STOCK_UPPER_HEIGHTS_IN.filter((h) => h <= slotIn + SAME_IN);
  return fits.length ? Math.max(...fits) : null;
}

/** A copy of `box` at `heightIn` — the box itself is never touched. */
const atHeight = (box, heightIn) => ({
  ...box,
  category: { ...(box.category || {}) },
  dimensions: { ...(box.dimensions || {}), y: heightIn * IN },
});

/** The made heights of `box` at its width, under a catalog family. */
function madeHeightsIn(family, box, ctx) {
  const kind = cabinetKindOf(family, box, ctx);
  const candidates = new Set(STOCK_UPPER_HEIGHTS_IN);
  for (const u of unitsOf(family, kind)) candidates.add(u.h);
  // A height is made when the box is — and, drawn with glass doors, the glass
  // door for it too (plan C16).
  const made = (h) => {
    const r = resolveCabinetSku(family, atHeight(box, h), ctx);
    return !!r.sku && (!r.glassDoor || !!r.glassDoor.sku);
  };
  return [...candidates].filter(made).sort((a, b) => a - b);
}

/**
 * Inches this box builds at with no height of its own: the run's band, then
 * the line's snap at the box's width (`snapUpperHeightIn`, the fill's own
 * rule). Null when the line makes nothing that fits.
 */
function runUpperHeightIn(run, box, band) {
  const bandIn = eighthIn(band.height);
  const family = familyOf(run);
  if (!familySizes(family) || !isSoldUpper(box)) return bandIn;
  const ctx = { profileId: profileOf(run).id };
  const kind = cabinetKindOf(family, box, ctx);
  const snapped = snapUpperHeightIn(
    family,
    kind,
    halfIn(box.dimensions.x),
    bandIn
  );
  return snapped ?? null;
}

/**
 * The height rule for one upper box in `run`, hanging in `band`.
 *
 * @returns {{ vendor: string|null, widthIn: number, runIn: number|null,
 *   madeIn: number[]|null, glass: boolean, roomIn: number|null,
 *   bandIn: number }} `glass`: the box has glass doors (its heights are the
 *   ones the line makes a glass door for);
 *   `madeIn` null for a family with no size catalog; `roomIn` null with no
 *   ceiling known (`band.room`).
 */
function upperHeightRule(run, box, band) {
  const family = familyOf(run);
  const catalog = !!familySizes(family) && isSoldUpper(box);
  const ctx = { profileId: profileOf(run).id };
  return {
    vendor: catalog ? family.vendor || family.name : null,
    widthIn: halfIn(box.dimensions.x),
    runIn: runUpperHeightIn(run, box, band),
    madeIn: catalog ? madeHeightsIn(family, box, ctx) : null,
    // the box has glass doors: what the line makes is what it makes glass for
    glass: catalog && box.doorStyle === 'glass-front',
    roomIn: roomInches(band.room),
    bandIn: eighthIn(band.height),
  };
}

/** The ceiling's room (metres, or null) in inches as the panel reads it. */
const roomInches = (roomM) => (roomM == null ? null : eighthIn(roomM));

/**
 * The one sentence a height the ceiling has no room for gets. The room is
 * said to the half inch BELOW it — "room for 38.5"" is true of a 38⅝" gap,
 * and a reader has no use for the eighths a scanned ceiling carries.
 */
const ceilingReason = (roomIn) =>
  `Doesn't fit under the ceiling — room for ${said(Math.floor(roomIn * 2 + 1e-9) / 2)}`;

/** Does `inches` fit in `roomIn` of ceiling room (null: no ceiling known)? */
const fitsUnderCeiling = (roomIn, inches) =>
  roomIn == null || inches <= roomIn + CEILING_SLACK_IN;

/** The run's height, or the band's when the line builds nothing there. */
const runLimitIn = (rule) => rule.runIn ?? rule.bandIn;

const sameIn = (a, b) => Math.abs(a - b) <= SAME_IN;

/** Why the LINE makes no box of this width `inches` tall; null when it does. */
function lineReason(rule, inches) {
  if (!rule.madeIn || rule.madeIn.some((h) => sameIn(h, inches))) return null;
  const w = `${said(rule.widthIn)}-wide${rule.glass ? ' glass' : ''}`;
  if (!rule.madeIn.length) return `${rule.vendor} makes no ${w} wall cabinet`;
  return `${rule.vendor} makes ${w} wall cabinets only ${listSaid(rule.madeIn)} tall`;
}

/**
 * Why this box cannot be `inches` tall — the line does not make it, the
 * ceiling has no room for it, or it is taller than the run — or null.
 */
function upperHeightUnavailableReason(rule, inches) {
  const line = lineReason(rule, inches);
  if (line) return line;
  if (!fitsUnderCeiling(rule.roomIn, inches)) return ceilingReason(rule.roomIn);
  const limit = runLimitIn(rule);
  if (inches > limit + SAME_IN) return `Taller than the run's ${said(limit)}`;
  return null;
}

/**
 * The chips for one upper's Height row, ascending: the stock heights, any
 * other height the line makes at this width, and the run's own — which
 * replaces its stock twin and is the chip lit when the box has no height of
 * its own (`id: 'run'`).
 */
function upperHeightChoices(rule) {
  const values = [...STOCK_UPPER_HEIGHTS_IN, ...(rule.madeIn || [])];
  if (rule.runIn != null) values.push(rule.runIn);
  const unique = [];
  for (const v of values.sort((a, b) => a - b)) {
    if (!unique.some((u) => sameIn(u, v))) unique.push(v);
  }
  return unique.map((inches) => {
    if (rule.runIn != null && sameIn(inches, rule.runIn))
      return { id: 'run', inches, isRun: true };
    const reason = upperHeightUnavailableReason(rule, inches);
    return {
      id: `h-${inches}`,
      inches,
      isRun: false,
      ...(reason ? { disabled: true, reason } : {}),
    };
  });
}

/**
 * What the fill builds for a stored bay height: the height itself when the
 * rule allows it; `{ heightM: null, reason }` when the line cannot build it
 * or the ceiling has no room for it (built at the run's height, and warned);
 * `{ heightM: null, reason: null }` when it is at or above the run's (it IS
 * the run's height, which needs no setting).
 */
function bayHeightFor(run, box, band, heightM) {
  const rule = upperHeightRule(run, box, band);
  const inches = heightM / IN;
  const line = lineReason(rule, halfIn(heightM));
  if (line) return { heightM: null, reason: line };
  if (!fitsUnderCeiling(rule.roomIn, inches))
    return { heightM: null, reason: ceilingReason(rule.roomIn) };
  if (inches >= runLimitIn(rule) - SAME_IN)
    return { heightM: null, reason: null };
  return { heightM, reason: null };
}

module.exports = {
  STOCK_UPPER_HEIGHTS_IN,
  offeredUpperHeightIn,
  CEILING_SLACK_IN,
  upperProbe,
  upperHeightRule,
  runUpperHeightIn,
  upperHeightChoices,
  upperHeightUnavailableReason,
  ceilingReason,
  fitsUnderCeiling,
  roomInches,
  bayHeightFor,
};
